import { Briefcase, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { Position } from '../types'
import { AvatarStack } from './AccessBits'

type Props = {
  rows: Position[]
  loading: boolean
  selectedId: string | null
  canWrite: boolean
  onSelect: (position: Position) => void
  onEdit: (position: Position) => void
  onDelete: (position: Position) => void
}

export function PositionsTable({ rows, loading, selectedId, canWrite, onSelect, onEdit, onDelete }: Props) {
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className="space-y-2 p-3">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-12" />
        ))}
      </div>
    )
  }

  const actions = (p: Position) =>
    canWrite ? (
      <div className="flex shrink-0 justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
        <Tooltip side="top" content={t('common.edit')}>
          <Button size="icon" variant="ghost" className="size-8" aria-label={t('common.edit')} onClick={() => onEdit(p)}>
            <Pencil />
          </Button>
        </Tooltip>
        <Tooltip side="top" content={t('common.delete')}>
          <Button size="icon" variant="ghost" className="size-8 text-muted-foreground hover:text-destructive" aria-label={t('common.delete')} onClick={() => onDelete(p)}>
            <Trash2 />
          </Button>
        </Tooltip>
      </div>
    ) : null

  return (
    <>
      <ul className="divide-y md:hidden">
        {rows.map((p) => (
          <li key={p.id} className={cn('space-y-3 p-3', selectedId === p.id && 'bg-brand-blue/[0.04]')} onClick={() => onSelect(p)}>
            <div className="flex items-start gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-blue/10 text-brand-blue">
                <Briefcase className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{p.title}</div>
                <div className="truncate text-xs text-muted-foreground">
                  <span className="font-mono">{p.code}</span>
                  {p.department ? ` · ${p.department.name}` : ''}
                </div>
              </div>
              {actions(p)}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pl-12">
              <Badge tone="neutral">{t(`access.positions.levels.${p.level}`)}</Badge>
              <AvatarStack people={p.employees.map((e) => ({ id: e.id, name: e.name, src: e.avatarUrl }))} />
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden md:block">
        <Table>
          <THead>
            <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
              <TH>{t('access.positions.columns.title')}</TH>
              <TH>{t('access.positions.columns.department')}</TH>
              <TH>{t('access.positions.columns.level')}</TH>
              <TH>{t('access.positions.columns.employees')}</TH>
              {canWrite ? <TH className="w-24 text-right">{t('access.positions.columns.actions')}</TH> : null}
            </TR>
          </THead>
          <TBody>
            {rows.map((p) => (
              <TR key={p.id} className={cn('cursor-pointer', selectedId === p.id && 'bg-brand-blue/[0.04]')} onClick={() => onSelect(p)}>
                <TD>
                  <div className="flex items-center gap-3">
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-blue/10 text-brand-blue">
                      <Briefcase className="size-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-medium">{p.title}</p>
                      <p className="max-w-64 truncate text-xs text-muted-foreground">
                        <span className="font-mono">{p.code}</span>
                        {p.description ? ` · ${p.description}` : ''}
                      </p>
                    </div>
                  </div>
                </TD>
                <TD className="text-muted-foreground">{p.department?.name ?? '—'}</TD>
                <TD>
                  <Badge tone="neutral">{t(`access.positions.levels.${p.level}`)}</Badge>
                </TD>
                <TD>
                  <AvatarStack people={p.employees.map((e) => ({ id: e.id, name: e.name, src: e.avatarUrl }))} max={4} />
                </TD>
                {canWrite ? <TD className="text-right">{actions(p)}</TD> : null}
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </>
  )
}
